import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import {
  useLazySearchSemenQuery,
  useSearchSemenQuery,
} from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Semen } from '@/rtk-query.gql';

import './SemenAutoComplete.css';

type SemenAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: Semen;
  inputValue?: string;
  setInputValue?: Dispatch<SetStateAction<string>>;
  ownerId?: number;
  fertSiteId?: string;
  exportId?: string;
  searchAllSemen?: boolean;
};

export const SemenAutoComplete = ({
  className,
  label,
  defaultValue,
  inputValue,
  setInputValue,
  ownerId,
  fertSiteId,
  exportId,
  searchAllSemen,
  ...autoCompleteProps
}: SemenAutoCompleteProps) => {
  const [filtered, setFiltered] = useState<Semen[]>([]);
  const [semen, setSemen] = useState<Semen[]>([]);
  const [setSelected] = useState<Semen | undefined>(defaultValue);
  const [query] = useLazySearchSemenQuery();
  const { data: allSemenData } = useSearchSemenQuery({
    search: '',
    selectionLimit: SELECTION_LIMIT,
  });

  useEffect(() => {
    if (allSemenData) {
      setSemen(allSemenData);
    }
  }, [allSemenData]);

  const filterSemen = useCallback(
    (query: string) => {
      const lowercaseQuery = query.toLowerCase();
      return semen.filter(
        (semen) => semen?.name?.toLowerCase().indexOf(lowercaseQuery) !== -1,
      );
    },
    [semen],
  );

  const search = useCallback(
    async (event: { query: string }) => {
      const queryText = event.query?.trim() || '';

      if (queryText.length < MIN_SEARCH_LENGTH && queryText !== '') {
        setFiltered(filterSemen(queryText));
        return;
      }

      const filters: Record<string, unknown> = {
        quantity: { gt: 0 },
      };

      if (queryText) {
        filters.name = { contains: queryText };
      }

      if (ownerId && !searchAllSemen) {
        filters.ownerId = { eq: ownerId };
      }

      if (fertSiteId && !searchAllSemen) {
        filters.storageLocation = {
          siteId: { eq: fertSiteId },
        };
      }
      if (exportId && !searchAllSemen) {
        filters.export = { eq: exportId };
      }

      const variables = {
        where: filters,
        selectionLimit: 100,
      };

      const data = await query(variables).unwrap();

      const formattedSemen = data.map((semen) => {
        const shortSiteName =
          semen.storageLocation?.site?.name?.split(' - ')[0] || '';
        return {
          ...semen,
          name: `${semen.name} - ${semen.colCode} [${semen.owner?.code}]${shortSiteName ? ` [${shortSiteName}]` : ''}`,
        };
      });

      setFiltered(formattedSemen);
    },
    [ownerId, searchAllSemen, fertSiteId, query, filterSemen],
  );
  const handleSelect = (e: { value: Semen }) => {
    setSelected(e.value);
    if (setInputValue) {
      setInputValue(e.value?.name as string);
    }
  };

  const handleChange = (e: { value: string }) => {
    if (setInputValue) {
      setInputValue(e.value as string);
    }
  };

  return (
    <div
      className={`[ semen-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
